import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type ReactElement } from "react";
import { ContentHeader, EmptyState, Field, Modal, QueryStatus, StatusBadge, useToast } from "@cx/ui";
import type { ListResult, LotListItem, WarehouseView } from "@cx/contracts";
import { formatDateBR, formatQty } from "@cx/domain";
import { can, type Permissions } from "@cx/db";
import { errorMessage } from "../../errors";
import { useSession } from "../../session";
import { LOT_STATUS_LABELS, LOT_STATUS_TONES, lotValidity } from "./labels";

const FILTERS = [
  ["all", "Todas as validades"],
  ["expired", "Vencidos"],
  ["expiring", "Vencendo"],
  ["valid", "Válidos"],
  ["no_expiry", "Sem validade"],
] as const;

/**
 * Suprimentos › Lotes (RF-SUP-07): lotes com saldo, do que vence antes para o que vence depois, com filtro pela
 * validade ("Vencendo" = nos próximos N dias) e por depósito; correção das datas do lote.
 */
export function LotsPage(): ReactElement {
  const { companyId, me, api } = useSession();
  const perms = me.permissions as Permissions;
  const canEdit = can(perms, "stock", "create");
  const [q, setQ] = useState("");
  const [status, setStatus] = useState<(typeof FILTERS)[number][0]>("all");
  const [days, setDays] = useState(30);
  const [warehouseId, setWarehouseId] = useState("");
  const [withStock, setWithStock] = useState(true);
  const [editing, setEditing] = useState<LotListItem | null>(null);
  const term = q.trim();
  const warehouses = useQuery({
    queryKey: ["warehouses", companyId],
    queryFn: () => api<{ data: WarehouseView[] }>("/warehouses"),
  });
  const list = useInfiniteQuery({
    queryKey: ["stock-lots", companyId, "list", term, status, days, warehouseId, withStock],
    initialPageParam: "",
    queryFn: ({ pageParam }) =>
      api<ListResult<LotListItem>>(
        `/stock/lots?${new URLSearchParams({
          status,
          days: String(days),
          withStock: String(withStock),
          ...(term ? { q: term } : {}),
          ...(warehouseId ? { warehouseId } : {}),
          ...(pageParam ? { cursor: pageParam } : {}),
        })}`,
      ),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
  const rows = list.data?.pages.flatMap((p) => p.data) ?? [];
  const count = list.data?.pages[0]?.summary.count;
  return (
    <>
      <ContentHeader title="Lotes" breadcrumb={["Suprimentos", "Lotes"]} />
      <div className="box card">
        <div className="row g-2 p-3 pb-0 align-items-center">
          <div className="col-md-4">
            <label className="visually-hidden" htmlFor="lots-q">
              Pesquisar lote
            </label>
            <input
              id="lots-q"
              type="search"
              className="form-control"
              placeholder="Lote, produto ou SKU"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>
          <div className="col-6 col-md-2">
            <label className="visually-hidden" htmlFor="lots-status">
              Validade
            </label>
            <select
              id="lots-status"
              className="form-select"
              value={status}
              onChange={(e) => setStatus(e.target.value as typeof status)}
            >
              {FILTERS.map(([v, l]) => (
                <option key={v} value={v}>
                  {l}
                </option>
              ))}
            </select>
          </div>
          <div className="col-6 col-md-2">
            <label className="visually-hidden" htmlFor="lots-days">
              Dias para vencer
            </label>
            <select
              id="lots-days"
              className="form-select"
              title="Janela do “Vencendo”"
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
            >
              {[7, 15, 30, 60, 90].map((d) => (
                <option key={d} value={d}>
                  Vence em até {d} dias
                </option>
              ))}
            </select>
          </div>
          <div className="col-6 col-md-2">
            <label className="visually-hidden" htmlFor="lots-warehouse">
              Depósito
            </label>
            <select
              id="lots-warehouse"
              className="form-select"
              value={warehouseId}
              onChange={(e) => setWarehouseId(e.target.value)}
            >
              <option value="">Todos os depósitos</option>
              {(warehouses.data?.data ?? []).map((w) => (
                <option key={w.id} value={w.id}>
                  {w.name}
                </option>
              ))}
            </select>
          </div>
          <div className="col-6 col-md-2">
            <div className="form-check">
              <input
                id="lots-with-stock"
                type="checkbox"
                className="form-check-input"
                checked={withStock}
                onChange={(e) => setWithStock(e.target.checked)}
              />
              <label className="form-check-label" htmlFor="lots-with-stock">
                Só com saldo
              </label>
            </div>
          </div>
        </div>
        <QueryStatus loading={list.isLoading} error={list.error} />
        {list.isSuccess && rows.length === 0 && (
          <EmptyState
            title="Nenhum lote"
            description="Marque “Controle de lote” na aba Estoque do produto: cada entrada pede o lote com fabricação e validade, e as saídas baixam primeiro o que vence antes."
            actions={[]}
          />
        )}
        {rows.length > 0 && (
          <>
            <div className="table-responsive" tabIndex={0} role="region" aria-label="Lotes (rolagem)">
              <table className="table align-middle mb-0" aria-label="Lotes">
                <thead>
                  <tr>
                    <th scope="col">Lote</th>
                    <th scope="col">Produto</th>
                    <th scope="col">Fabricação</th>
                    <th scope="col">Validade</th>
                    <th scope="col">Situação</th>
                    <th scope="col" className="text-end">
                      Saldo
                    </th>
                    {canEdit && (
                      <th scope="col">
                        <span className="visually-hidden">Ações</span>
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((l) => (
                    <tr key={l.id}>
                      <td className="fw-semibold">{l.code}</td>
                      <td>
                        {l.productName}
                        {l.productSku && <span className="text-muted small ms-2">{l.productSku}</span>}
                      </td>
                      <td>{l.manufacturedAt ? formatDateBR(l.manufacturedAt) : "—"}</td>
                      <td>{lotValidity(l.expiresAt, l.daysToExpire)}</td>
                      <td>
                        <StatusBadge tone={LOT_STATUS_TONES[l.status]}>
                          {LOT_STATUS_LABELS[l.status]}
                        </StatusBadge>
                      </td>
                      <td className="text-end qty">
                        {formatQty(l.physicalQ4)} {l.unit}
                      </td>
                      {canEdit && (
                        <td className="text-end">
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary"
                            aria-label={`Corrigir datas do lote ${l.code} de ${l.productName}`}
                            onClick={() => setEditing(l)}
                          >
                            Corrigir datas
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 p-3">
              {count !== undefined && count >= 0 && (
                <span className="text-muted small" data-testid="lots-summary">
                  {count} {count === 1 ? "lote" : "lotes"}
                </span>
              )}
              {list.hasNextPage && (
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  disabled={list.isFetchingNextPage}
                  onClick={() => list.fetchNextPage()}
                >
                  Carregar mais
                </button>
              )}
            </div>
          </>
        )}
      </div>
      {editing && <LotDatesDialog lot={editing} onClose={() => setEditing(null)} />}
    </>
  );
}

/** Correção das datas do lote (digitação errada na entrada); o código e os saldos não mudam. */
function LotDatesDialog({ lot, onClose }: { lot: LotListItem; onClose(): void }) {
  const { companyId, api } = useSession();
  const qc = useQueryClient();
  const toast = useToast();
  const [manufacturedAt, setMfg] = useState(lot.manufacturedAt ?? "");
  const [expiresAt, setExp] = useState(lot.expiresAt ?? "");
  const problem =
    manufacturedAt && expiresAt && expiresAt < manufacturedAt
      ? "A validade não pode ser anterior à fabricação."
      : null;
  const save = useMutation({
    mutationFn: () =>
      api(`/stock/lots/${lot.id}`, {
        method: "PUT",
        body: JSON.stringify({ manufacturedAt: manufacturedAt || null, expiresAt: expiresAt || null }),
      }),
    onSuccess: () => {
      toast("success", `Datas do lote ${lot.code} corrigidas`);
      qc.invalidateQueries({ queryKey: ["stock-lots", companyId] });
      onClose();
    },
    onError: (e: Error) => toast("danger", errorMessage(e)),
  });
  return (
    <Modal
      open
      title={`Lote ${lot.code} · ${lot.productName}`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-outline-secondary" onClick={onClose}>
            Voltar
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={!!problem || save.isPending}
            onClick={() => save.mutate()}
          >
            Salvar
          </button>
        </>
      }
    >
      <div className="row">
        <Field label="Fabricação" className="col-sm-6">
          <input type="date" value={manufacturedAt} onChange={(e) => setMfg(e.target.value)} />
        </Field>
        <Field label="Validade" className="col-sm-6" error={problem ?? undefined}>
          <input type="date" value={expiresAt} onChange={(e) => setExp(e.target.value)} />
        </Field>
      </div>
    </Modal>
  );
}
